/**
 * Variant quantity rules driven by metafields:
 * - `custom.quantity_selector_increment` for step (defaults to 1)
 * - `custom.minimum_order_quantity` to set the minimum quantity (falls back to step)
 * - `custom.maximum_order_quantity` to set an explicit maximum (falls back to inventory limits)
 */
(() => {
  if (window.__audioAdvisorQuantityRulesInitialized) {
    return;
  }
  window.__audioAdvisorQuantityRulesInitialized = true;

  const variantRulesRegistry = {};

  const QuantityRules = {
    /**
     * Merge rules from all JSON script tags under root into the persistent registry.
     * @param {ParentNode} [root=document]
     * @returns {object}
     */
  ingestRulesScripts(root = document) {
    root.querySelectorAll('.js-variant-quantity-rules').forEach((script) => {
      try {
        Object.assign(variantRulesRegistry, JSON.parse(script.textContent));
      } catch (error) {
        // Ignore malformed JSON blocks.
      }
    });

    return variantRulesRegistry;
  },

  /**
   * @returns {object}
   */
  getRulesMap() {
    return variantRulesRegistry;
  },

  /**
   * @param {HTMLInputElement} input
   * @returns {string|null}
   */
  resolveVariantId(input) {
    if (input.dataset.variantId) return input.dataset.variantId;

    const form = input.closest('form');
    const idInput = form?.querySelector('[name="id"]');
    if (idInput?.value) return idInput.value;

    const lineItem = input.closest('.cart-item, [data-variant-id]');
    if (lineItem?.dataset?.variantId) return lineItem.dataset.variantId;

    return null;
  },

  /**
   * Applies variant map rules onto the input element data attributes.
   * @param {HTMLInputElement} input
   * @param {string|null} [variantId]
   * @returns {object|null}
   */
  syncInputFromVariantMap(input, variantId = null) {
    const scope = input.closest('product-form, cart-items, .js-product, .shopify-section') || document;
    QuantityRules.ingestRulesScripts(scope);

    const resolvedVariantId = variantId || QuantityRules.resolveVariantId(input);
    if (!resolvedVariantId) return null;

    input.dataset.variantId = resolvedVariantId;

    const map = QuantityRules.getRulesMap();
    const mapRules = map[resolvedVariantId] || map[String(resolvedVariantId)] || null;
    if (!mapRules) return null;

    input.dataset.qtyStep = String(mapRules.step);
    input.dataset.qtyMin = String(mapRules.min);

    if (mapRules.max) {
      input.max = mapRules.max;
    } else {
      input.removeAttribute('max');
    }

    return mapRules;
  },

  /**
   * @param {HTMLInputElement} input
   * @returns {{ min: number, step: number, max: number|null, allowZero: boolean, hasCustomRules: boolean, variantId: string|null, mapRules: object|null }}
   */
  fromInput(input) {
    QuantityRules.syncInputFromVariantMap(input);

    const step = Math.max(1, parseInt(input.dataset.qtyStep || input.step || '1', 10) || 1);
    const rawMin = parseInt(input.dataset.qtyMin || input.min || '1', 10) || 1;
    const allowZero = (input.dataset.qtyAllowZero === 'true' || input.min === '0') && rawMin === 0;
    const min = allowZero ? Math.max(0, rawMin) : Math.max(1, rawMin);
    const max = input.max !== '' && input.max != null ? parseInt(input.max, 10) : null;
    const variantId = QuantityRules.resolveVariantId(input);
    const map = QuantityRules.getRulesMap();
    const mapRules = variantId ? (map[variantId] || map[String(variantId)] || null) : null;

    return {
      min,
      step,
      max: Number.isFinite(max) ? max : null,
      allowZero,
      hasCustomRules: step > 1 || min > 1,
      variantId,
      mapRules
    };
  },

  /**
   * @param {number|string} qty
   * @param {{ min: number, step: number, max: number|null, allowZero: boolean }} rules
   * @returns {boolean}
   */
  isValid(qty, rules) {
    const value = parseInt(qty, 10);
    if (!Number.isFinite(value)) return false;
    if (rules.allowZero && value === 0) return true;
    if (value < rules.min) return false;
    if (rules.max !== null && value > rules.max) return false;
    return (value - rules.min) % rules.step === 0;
  },

  /**
   * @param {number|string} qty
   * @param {{ min: number, step: number, max: number|null, allowZero: boolean }} rules
   * @param {'nearest'|'up'|'down'} [mode='nearest']
   * @returns {number}
   */
  snap(qty, rules, mode = 'nearest') {
    let value = parseInt(qty, 10);

    if (!Number.isFinite(value)) {
      value = rules.allowZero ? 0 : rules.min;
    }

    if (rules.allowZero && value === 0) return 0;

    if (value < rules.min) return rules.min;

    if (rules.max !== null && value > rules.max) {
      value = rules.max;
    }

    const remainder = (value - rules.min) % rules.step;
    if (remainder === 0) return value;

    if (mode === 'up') {
      value += rules.step - remainder;
    } else if (mode === 'down') {
      value -= remainder;
    } else if (remainder >= rules.step / 2) {
      value += rules.step - remainder;
    } else {
      value -= remainder;
    }

    if (value < rules.min) value = rules.min;

    if (rules.max !== null && value > rules.max) {
      const steppedDown = value - ((value - rules.min) % rules.step || rules.step);
      value = steppedDown >= rules.min ? steppedDown : rules.min;
    }

    return value;
  },

  /**
   * @param {number|string} current
   * @param {{ min: number, step: number, max: number|null, allowZero: boolean }} rules
   * @returns {number}
   */
  increment(current, rules) {
    const value = parseInt(current, 10);
    const base = Number.isFinite(value) ? value : rules.min;
    if (base === 0) return rules.min;

    const next = base + rules.step;
    if (rules.max !== null && next > rules.max) return base;
    return next;
  },

  /**
   * @param {number|string} current
   * @param {{ min: number, step: number, max: number|null, allowZero: boolean }} rules
   * @returns {number}
   */
  decrement(current, rules) {
    const value = parseInt(current, 10);
    const base = Number.isFinite(value) ? value : rules.min;

    if (base <= rules.min) return rules.min;

    const next = base - rules.step;
    return next < rules.min ? rules.min : next;
  },

  /**
   * @param {HTMLInputElement} input
   * @param {'nearest'|'up'|'down'} [mode='nearest']
   * @returns {number|null}
   */
  applyToInput(input, mode = 'nearest') {
    const rules = QuantityRules.fromInput(input);
    const snapped = QuantityRules.snap(input.value, rules, mode);
    const changed = String(snapped) !== String(input.value);

    input.value = snapped;
    input.min = rules.allowZero ? '0' : String(rules.min);
    input.step = String(rules.step);
    input.dataset.qtyStep = String(rules.step);
    input.dataset.qtyMin = String(rules.min);
    input.dataset.qtyAllowZero = rules.allowZero ? 'true' : 'false';

    if (rules.max !== null) {
      input.max = String(rules.max);
    } else {
      input.removeAttribute('max');
    }

    return changed ? snapped : null;
  }
};

window.QuantityRules = QuantityRules;

(() => {
  const BOUND_FLAG = '__quantityRulesBound';

  /**
   * @param {HTMLElement} quantityInput
   */
  function bindQuantityInput(quantityInput) {
    if (quantityInput[BOUND_FLAG]) return;
    quantityInput[BOUND_FLAG] = true;

    const input = quantityInput.querySelector('.qty-input__input');
    if (!input) return;

    const rules = QuantityRules.fromInput(input);
    
    if (rules.hasCustomRules) {
      QuantityRules.applyToInput(input);
    }

    input.addEventListener('blur', () => {
      const before = input.value;
      const changed = QuantityRules.applyToInput(input);
      
      if (changed !== null && before !== input.value) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
        quantityInput.currentQty = input.value;
      }
    });

    input.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Enter') return;
      const before = input.value;
      const changed = QuantityRules.applyToInput(input);
      if (changed !== null && before !== input.value) {
        quantityInput.currentQty = input.value;
      }
    });
  }

  /**
   * Intercepts +/- clicks before QuantityInput's bound handler (which uses stepUp/stepDown).
   * @param {MouseEvent} evt
   */
  function handleQuantityButtonClick(evt) {
    const btn = evt.target.closest('.qty-input__btn');
    if (!btn || btn.disabled) return;

    const quantityInput = btn.closest('quantity-input');
    const input = quantityInput?.querySelector('.qty-input__input');
    if (!input) return;

    const rules = QuantityRules.fromInput(input);
    if (!rules.hasCustomRules) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();

    const before = input.value;

    if (btn.name === 'plus') {
      input.value = QuantityRules.increment(before, rules);
    } else {
      input.value = QuantityRules.decrement(before, rules);
    }

    if (input.value !== before) {
      input.dispatchEvent(new Event('change', { bubbles: true }));
      quantityInput.currentQty = input.value;
    }
  }

  function bindAllQuantityInputs(root = document) {
    root.querySelectorAll('quantity-input').forEach((el) => bindQuantityInput(el));
  }

  function handleVariantChange(evt) {
    setTimeout(() => {
      const form = evt.detail?.form;
      if (!form) return;

      const input = form.querySelector('.qty-input__input[name="quantity"]');
      if (!input) return;

      if (evt.detail?.variant?.id) {
        QuantityRules.syncInputFromVariantMap(input, String(evt.detail.variant.id));
      }

      const before = input.value;
      const rules = QuantityRules.fromInput(input);
      input.value = String(rules.min);
      input.min = rules.allowZero ? '0' : String(rules.min);
      input.step = String(rules.step);
      input.dataset.qtyStep = String(rules.step);
      input.dataset.qtyMin = String(rules.min);
      input.dataset.qtyAllowZero = rules.allowZero ? 'true' : 'false';
      if (rules.max !== null) {
        input.max = String(rules.max);
      } else {
        input.removeAttribute('max');
      }
      const changed = before !== input.value;

      if (before !== input.value) {
        const quantityInput = input.closest('quantity-input');
        if (quantityInput) quantityInput.currentQty = input.value;
      }
    }, 20);
  }

  function init() {
    const rulesScript = document.querySelector('.js-variant-quantity-rules');
    if (rulesScript) {
      try {
        const map = JSON.parse(rulesScript.textContent);
      } catch (error) {
      }
    }

    bindAllQuantityInputs();

    const initialProductQuantityInput = document.querySelector('.js-product-form .qty-input__input[name="quantity"]');
    if (initialProductQuantityInput) {
      QuantityRules.syncInputFromVariantMap(initialProductQuantityInput);
      const rules = QuantityRules.fromInput(initialProductQuantityInput);
      initialProductQuantityInput.value = String(rules.min);
      initialProductQuantityInput.min = rules.allowZero ? '0' : String(rules.min);
      initialProductQuantityInput.step = String(rules.step);
      initialProductQuantityInput.dataset.qtyStep = String(rules.step);
      initialProductQuantityInput.dataset.qtyMin = String(rules.min);
      initialProductQuantityInput.dataset.qtyAllowZero = rules.allowZero ? 'true' : 'false';
      if (rules.max !== null) {
        initialProductQuantityInput.max = String(rules.max);
      } else {
        initialProductQuantityInput.removeAttribute('max');
      }
      const quantityInputParent = initialProductQuantityInput.closest('quantity-input');
      if (quantityInputParent) quantityInputParent.currentQty = initialProductQuantityInput.value;
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches?.('quantity-input')) bindQuantityInput(node);
          bindAllQuantityInputs(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('click', handleQuantityButtonClick, true);
    document.addEventListener('on:variant:change', handleVariantChange);

    document.addEventListener('change', (evt) => {
      if (!evt.target.matches('.qty-input__input')) return;

      const rules = QuantityRules.fromInput(evt.target);
      if (!rules.hasCustomRules) return;

      const before = evt.target.value;
      const changed = QuantityRules.applyToInput(evt.target);

      if (changed !== null) {
        const quantityInput = evt.target.closest('quantity-input');
        if (quantityInput) quantityInput.currentQty = evt.target.value;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
})();
